import Image from '@atlaskit/image';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
  getLoadingProgress,
  setLoadingProgress,
} from '@databases/database-state/state/initial-database-loading-state';
import React, { useEffect, useState } from 'react';

import { getBaseUrl } from './get-base-url';

type ColorMode = 'light' | 'dark';

const containerStyles = xcss({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  position: 'absolute',
  inset: 'space.0',
});

const CONTAINER_MARGIN_TOP = '160px';

const contentStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'space.250',
  marginTop: 'space.1000',
});

const progressBarContainerStyles = xcss({
  width: '172px',
  height: '6px',
  borderRadius: 'radius.small',
  overflow: 'hidden',
});

const PROGRESS_BAR_CONTAINER_BG = token('color.background.neutral', '#091E420F');

const PROGRESS_BAR_INDICATOR_COLOR = token(
  'color.background.accent.gray.subtler.hovered',
  '#B7B9BE'
);

const progressBarIndicatorStyles = xcss({
  height: '100%',
  borderRadius: 'radius.small',
  transition: 'width 1s ease-out',
});

interface ProgressBarProps {
  value: number;
}

const ProgressBar = ({ value }: ProgressBarProps) => (
  <Box
    xcss={progressBarContainerStyles}
    role="progressbar"
    style={{ backgroundColor: PROGRESS_BAR_CONTAINER_BG }}
  >
    <Box
      xcss={progressBarIndicatorStyles}
      style={{
        width: `${value * 100}%`,
        backgroundColor: PROGRESS_BAR_INDICATOR_COLOR,
      }}
    />
  </Box>
);

const getColorMode = (): ColorMode => {
  const colorMode = document.documentElement.getAttribute('data-color-mode');

  return colorMode === 'dark' ? 'dark' : 'light';
};

export const DatabaseInitialLoadingState = () => {
  const colorMode = getColorMode();
  const baseUrl = getBaseUrl();
  const imageSrc =
    colorMode === 'dark'
      ? `${baseUrl}static/loading-placeholder-dark.gif`
      : `${baseUrl}static/loading-placeholder-light.gif`;

  const [progress, setProgress] = useState(() => getLoadingProgress());

  useEffect(() => {
    if (getLoadingProgress() >= 0.9) {
      return;
    }

    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const scheduleStep = (value: number, delay: number) => {
      const timeout = setTimeout(() => {
        setLoadingProgress(value);
        setProgress(value);
      }, delay);
      timeouts.push(timeout);
    };

    const animationFrameId = requestAnimationFrame(() => {
      scheduleStep(0.25, 0);
      scheduleStep(0.5, 800);
      scheduleStep(0.9, 1600);
    });

    return () => {
      cancelAnimationFrame(animationFrameId);
      timeouts.forEach(clearTimeout);
    };
  }, []);

  return (
    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
    <Box xcss={containerStyles} style={{ marginTop: CONTAINER_MARGIN_TOP }}>
      <Stack xcss={contentStyles}>
        <Image src={imageSrc} alt="" width="48" height="48" />
        <ProgressBar value={progress} />
      </Stack>
    </Box>
  );
};
